import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import { ReactNode } from 'react';

import { ReactComponent as X } from 'assets/icons/X.svg';

import { API_KEYS_MODAL_WRAPPER_ID } from './constants';

interface ApiKeyResultPopupProps {
  title: string;
  subtitle: string;
  finishLabel: string;
  closeLabel: string;
  onFinish: () => void;
  dataTestId?: string;
  finishTestId?: string;
  children?: ReactNode;
}

/**
 * Shared shell for the post-action result modals (Figma 6392:67419). The "API key
 * created" modal passes the secret field + warning as `children`; the "API key
 * revoked" modal reuses the same shell with no children.
 */
const ApiKeyResultPopup = ({
  title,
  subtitle,
  finishLabel,
  closeLabel,
  onFinish,
  dataTestId,
  finishTestId,
  children,
}: ApiKeyResultPopupProps) => (
  <Popup isVisible closePopup={onFinish} isViewportScrollable wrapperId={API_KEYS_MODAL_WRAPPER_ID}>
    <Popup.Container
      className="shadow-modal !max-w-[calc(100vw-2rem)] md:w-[512px] md:!max-w-[512px]"
      dataTestId={dataTestId}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pb-4 pt-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-grayscale-black text-size-md font-medium">{title}</span>
          <span className="text-gray-80 text-size-md font-light">{subtitle}</span>
        </div>
        <button
          type="button"
          onClick={onFinish}
          aria-label={closeLabel}
          className="hover:bg-gray-30 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl p-2.5"
        >
          <X className="stroke-grayscale-black h-5 w-5 stroke-2" />
        </button>
      </div>

      <Popup.Divider />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-4">{children}</div>

      <Popup.Divider />

      <div className="flex shrink-0 justify-end px-4 pb-4 pt-4">
        <Button variant="secondaryTertiary" size="md" onClick={onFinish} data-testid={finishTestId}>
          {finishLabel}
        </Button>
      </div>
    </Popup.Container>
  </Popup>
);

export default ApiKeyResultPopup;
